import { api } from "@/lib/apiClient";

export interface ErrorLogCodeFrame {
  file: string;
  file_path?: string;
  line: number;
  func: string;
}

export type ErrorSeverity = "kritisch" | "warnung" | "unkritisch";
export type HealthStatus = ErrorSeverity | "ok";

export interface ErrorLogEntry {
  fingerprint: string;
  severity: ErrorSeverity;
  severity_source: "manual" | "auto";
  severity_classified_by: string | null;
  severity_note: string | null;
  exception_type: string;
  exception_message: string;
  user_code_file: string | null;
  user_code_line: number | null;
  user_code_func: string | null;
  user_code_chain: ErrorLogCodeFrame[];
  request_method: string | null;
  request_path: string | null;
  request_status: number | null;
  first_seen: string;
  last_seen: string;
  occurrences: number;
  log_files: string[];
  acknowledged: boolean;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  sample_traceback?: string;
  latest_ai_analysis?: ErrorLogAiAnalysis | null;
  latest_cursor_prompt?: ErrorLogAiAnalysis | null;
}

export interface ErrorLogAiAnalysis {
  id: number;
  model: string;
  analysis: string;
  created_at: string | null;
  created_by: string;
}

export interface ErrorLogSummary {
  open_count: number;
  total_count: number;
  total_occurrences: number;
  newest_at: string | null;
  log_dir: string;
  ai_enabled: boolean;
  status: HealthStatus;
  severity_counts: Record<ErrorSeverity, number>;
}

export interface ErrorLogListResponse {
  errors: ErrorLogEntry[];
  log_files_scanned: string[];
  parsed_at: string;
  ai_enabled: boolean;
}

export interface DbPoolStats {
  min: number;
  max: number;
  used: number | null;
  free: number | null;
}

export interface RealtimeStatsResponse {
  generated_at: string;
  realtime: Record<string, unknown>;
  db_pools: Record<string, DbPoolStats>;
  offpaper_sync_subscribers: number;
  offpaper_sync_queue_overflow_count: number;
}

export async function fetchErrorLogSummary(): Promise<ErrorLogSummary> {
  return api.get<ErrorLogSummary>("/admin/error-logs/summary");
}

export async function fetchErrorLogs(includeAcknowledged = false): Promise<ErrorLogListResponse> {
  const query = includeAcknowledged ? "?include_acknowledged=true" : "";
  return api.get<ErrorLogListResponse>(`/admin/error-logs${query}`);
}

export async function fetchErrorLogDetail(fingerprint: string): Promise<ErrorLogEntry> {
  return api.get<ErrorLogEntry>(`/admin/error-logs/${encodeURIComponent(fingerprint)}`);
}

export async function acknowledgeErrorLog(fingerprint: string, note?: string): Promise<void> {
  await api.post(`/admin/error-logs/${encodeURIComponent(fingerprint)}/ack`, note ? { note } : {});
}

export async function unacknowledgeErrorLog(fingerprint: string): Promise<void> {
  await api.post(`/admin/error-logs/${encodeURIComponent(fingerprint)}/unack`, {});
}

export async function refreshErrorLogs(): Promise<{
  ok: boolean;
  open_count: number;
  total_count: number;
  log_files_scanned: string[];
}> {
  return api.post("/admin/error-logs/refresh", {});
}

export async function requestErrorLogAiAnalysis(fingerprint: string): Promise<{
  fingerprint: string;
  id: number;
  model: string;
  analysis: string;
  created_at: string | null;
  created_by: string;
}> {
  return api.post(`/admin/error-logs/${encodeURIComponent(fingerprint)}/ai`, {});
}

export async function setErrorLogSeverity(
  fingerprint: string,
  severity: ErrorSeverity,
  note?: string,
): Promise<void> {
  await api.post(`/admin/error-logs/${encodeURIComponent(fingerprint)}/severity`, {
    severity,
    ...(note ? { note } : {}),
  });
}

export async function resetErrorLogSeverity(fingerprint: string): Promise<void> {
  await api.del(`/admin/error-logs/${encodeURIComponent(fingerprint)}/severity`);
}

export interface CursorPromptResult {
  fingerprint: string;
  id: number;
  model: string;
  prompt: string;
  created_at: string | null;
  created_by: string;
}

export async function requestCursorPrompt(fingerprint: string): Promise<CursorPromptResult> {
  return api.post(`/admin/error-logs/${encodeURIComponent(fingerprint)}/cursor-prompt`, {});
}

export async function fetchRealtimeStats(): Promise<RealtimeStatsResponse> {
  return api.get<RealtimeStatsResponse>("/admin/realtime/stats");
}
